import store from "@/store";
import router from "@/router";
import { Token_LogOut, Token_SignIn } from "@/apis/api";
import { openLoginDialog } from "@/utils/loginDialog";
import { loadSavedLogin } from "@/utils/savedLogin";
import { markLoginMethod } from "@/utils/knownAccounts";
import { clearAccountDrafts } from "@/utils/accountStorage";

/*
  切換帳號（2026-10-30）。清單在 utils/knownAccounts.js，畫面在 components/Common/AccountSwitchDialog.vue。

  做法是「登出目前的帳號、再登入另一個」，不是同時保持多個帳號登入 ——
  登入憑證（refresh token）是一張 .kf-9595.com 的 Cookie，平台、社群、AI 三站共用，一次只能是一個帳號。

  1. 解除這支手機的推播（要帶著目前的登入身分，所以先做）
  2. 請後端登出，撤銷這次登入的整組憑證。**失敗就不切換**：舊憑證沒撤銷的話，
     社群、AI 站手上的 access token 還能用，會繼續顯示舊帳號（最長 24 小時）。
     撤銷了的話，兩站下次打平台 API 會被拒絕、用新的 Cookie 換票，自己就換成新帳號
  3. 清本機：登入狀態、升級表單草稿、登入後要導回的網址
  4. 登入目標帳號：
       - App 存了那個帳號的密碼 → 直接登入、重新載入
       - 其他（網頁、沒存密碼、密碼改過、Google 帳號）→ 開登入框、帶入帳號，
         Google 帳號照平常按「以 Google 帳號繼續」。登入完整頁重新載入；關掉沒登入就回首頁
*/

/** 切換失敗、而且還是原本的帳號（訊息可以直接顯示給使用者） */
export class SwitchError extends Error {}

async function signOutForSwitch() {
  const { unregisterPush, ensurePushRegistered } = await import("@/utils/push");
  await unregisterPush();
  try {
    await Token_LogOut();
  } catch (e) {
    // 還是原本的帳號：推播綁回來
    ensurePushRegistered().catch(() => {});
    throw new SwitchError("連不上伺服器，帳號沒有切換，請稍後再試。");
  }
  await store.dispatch("SignOut", { callApi: false });
  clearAccountDrafts();
  try {
    localStorage.removeItem("redirectAfterLogin");
  } catch (e) {
    /* 略過 */
  }
}

/** App 存了這個帳號的密碼就直接登入；成功回 true */
async function signInWithSavedPassword(email) {
  const { password } = await loadSavedLogin(email);
  if (!password) return false;
  try {
    // 只有 App 存得到密碼；App 不跑 reCAPTCHA，後端看 User-Agent 放行（同 SignIn.vue）
    const res = await Token_SignIn({ email, password, recaptchaToken: "APP_NATIVE" });
    localStorage.setItem("_token", res.data.token);
    markLoginMethod("password");
    return true;
  } catch (e) {
    return false; // 密碼改過、帳號停用…：改開登入框
  }
}

/**
 * 切換到清單裡的某個帳號；account 給 null 是「登入其他帳號」。
 * 回傳 "reload"（已經登入、頁面重新載入中）或 "dialog"（開了登入框）。
 * 登出失敗會丟 SwitchError（訊息可以直接顯示）。
 */
export async function switchToAccount(account) {
  await signOutForSwitch();

  if (account && account.method !== "google" && (await signInWithSavedPassword(account.email))) {
    window.location.reload();
    return "reload";
  }

  /*
      要登入才能看的頁面（例如個人中心）：先回首頁再開登入框，登入完再回來。
      留在原頁的話，已經登出了，頁面上任何一個請求被回 401，
      api.js 的 finalizeUnauthorized 就會關掉登入框、換到登入頁。
  */
  const here = router.currentRoute;
  const needsAuth = here.matched.some((record) => record.meta && record.meta.requiresAuth);
  const after = needsAuth ? here.fullPath : null;
  if (needsAuth) await router.replace("/").catch(() => {});

  if (!account) {
    openLoginDialog({ blank: true, reload: true, after });
    return "dialog";
  }
  openLoginDialog({
    email: account.email,
    reload: true,
    after,
    notice: account.method === "google" ? "這個帳號用 Google 登入，請按「以 Google 帳號繼續」。" : null,
  });
  return "dialog";
}
